<template>
  <div>
    <ColorSwatches
      v-model="selectedColorId"
      :name="id"
      v-bind="colorProps"
      :show-dropdown="shouldDisplayColorPicker"
      :is-expandable="isMcom"
      :color-label="colorLabel"
      :is-spacing-enabled="isSpacingExperimentEnabled"
      :is-spacing-2-enabled="spacingExperiment2ForBCOM"
      :is-colorway-pricing="isColorwayPricing"
      :is-colorway-pricing2="isColorwayPricing2"
      :is-mobile="isMobile"
      :class="{'cosmetic-exp': isSpacingExpForBcom}"
      @update:modelValue="fireTag"
    >
      <template #before-title>
        <div
          v-if="isFindationApplicable"
          data-el="findation"
          data-testid="findation"
          class="findation-container margin-bottom-xs opacity-z"
        >
          <div
            id="findation-widget-button"
            ref="findationButton"
          />
        </div>
      </template>

      <template #before-swatches>
        <div
          v-if="shouldShowShadeText"
          class="findation-message-container small margin-bottom-xs margin-top-xs"
        >
          <div class="findation-selection-confirmation confirmation-black-filled" />
          <span class="findation-selection-msg">This shade is your <strong>best match</strong>.</span>
        </div>
      </template>

      <template #swatch-icon="{ color }">
        <span
          v-if="bestShadeColorId === color.id"
          class="findation-selected confirmation-black-filled"
        />
      </template>
    </ColorSwatches>
    <div
      v-if="isBTCustomColorSiteletEligible || orderSwatches"
      class="buttons-container margin-top-xs"
    >
      <ViewCustomColors v-if="isBTCustomColorSiteletEligible" />
      <OrderSwatches v-if="orderSwatches" />
    </div>
  </div>
</template>

<script setup>
/* istanbul ignore file */
import { ref, computed, onMounted } from 'vue';
import { ColorSwatches } from '@common-vue/media-card-large/src/modules/color';
import { useBrand } from '@composables/useBrand';
import { useExperiments } from '@composables/useExperiments';
import { useDeviceType } from '@composables/utils/useDeviceType';
import { useFeatureEligibility } from '@modules/featureControl';
import useFindation from '@composables/useFindation';
import ViewCustomColors from '@components/viewCustomColors/ViewCustomColors';
import { useColor } from '@modules/color/composables';
import { OrderSwatches } from '@modules/order-swatches';
import tagColorChange from '@composables/analytics/color';

const props = defineProps({
  id: {
    type: String,
    required: true,
  },
  colorProps: {
    /** @type {import('vue').PropType<ColorProps>} */
    type: Object,
    default: () => ({
      colors: [],
      spriteUrls: [],
      priceToColors: [],
    }),
  },
  colorLabel: {
    type: String,
    default: 'Color',
  },
});

const { selectedColorId } = useColor(props.id);

const bestShadeColorId = ref(0);
const findationButton = ref(null);

const { isMcom, isBcom } = useBrand();
const { isMobile } = useDeviceType();

const {
  experimentFlags: {
    isSpacingExperimentEnabled, isSpacingExperiment2Enabled, isColorwayPricing, isColorwayPricing2,
  },
} = useExperiments();

const isSpacingExpForBcom = isBcom && isSpacingExperimentEnabled;

const shouldShowShadeText = computed(
  () => isMcom && bestShadeColorId.value && bestShadeColorId.value === selectedColorId.value,
);

const spacingExperiment2ForBCOM = isBcom && isSpacingExperiment2Enabled;

/**
 *
 * @param {string} matchingColor
 */
const findationCallback = (matchingColor) => {
  if (matchingColor) {
    /** @type {{id: number} | undefined}  */
    const match = props.colorProps.colors.find(
      (color) => color.name?.includes(matchingColor) || color.name?.includes(matchingColor.split(' ')?.[1]),
    );
    if (match?.id) {
      bestShadeColorId.value = match.id;
      selectedColorId.value = match.id;
    }
  }
};

const { initFindation, isFindationApplicable } = useFindation(findationCallback);

const {
  isBTCustomColorSiteletEligible, orderSwatches, shouldDisplayColorPicker,
} = useFeatureEligibility();

onMounted(() => {
  /** @type {HTMLElement | null | undefined} */
  initFindation(findationButton.value);
});

// We use @update:modelValue for fireTag so it only listens to when user clicks on color
const { fireTag } = tagColorChange({ id: props.id });
</script>

<style lang="scss" scoped>
@import "~settingSCSS";
</style>

<style lang="scss" brand="mcom" scoped>
@import '@csgIcons/_confirmation-black-filled.scss';

.findation-container.opacity-z {
  opacity: 0;
}
.findation-message-container {
  display: flex;
  flex-flow: row nowrap;
}
.findation-selection-confirmation {
  margin-right: 5px;
  width: 16px;
  height: 16px;
  background-size: contain;
}
.findation-selected {
  position: absolute;
  left: 51%;
  top: 15%;
  width: 16px;
  height: 16px;
  background-size: contain;
  z-index: 2;
}
.buttons-container {
  display: flex;
}
</style>

<style lang="scss" brand="bcom" scoped>
.cosmetic-exp {
  :deep() {
    .colors-form {
      .selected {
        border: $thick-black-border;
        padding: 0;
      }

      .color-swatch-sprite-wrapper.updated-wrapper {
        width: 40px;
        min-width: 40px;
        height: 40px;
      }

      .color-swatch-sprite-label.updated-label {
        width: 30px;
        height: 30px;
      }

    }
  }
}
</style>
